import { useCallback, useEffect, useState } from 'react';
import { useApi } from './useApi';
import { Icd10Code, NhrplCode } from '../types/codes';

/**
 * Cache storage on window to guarantee singleton even if webpack bundles
 * this module twice (different import paths, HMR, etc.).
 */
interface CodeCacheStore {
  icd10: Icd10Code[] | null;
  icd10Promise: Promise<Icd10Code[]> | null;
  icd10Index: Map<string, Icd10Code[]> | null;
  nhrpl: NhrplCode[] | null;
  nhrplPromise: Promise<NhrplCode[]> | null;
}

const CACHE_KEY = '__ikhangynae_code_cache__';

function getStore(): CodeCacheStore {
  const w = window as unknown as Record<string, unknown>;
  if (!w[CACHE_KEY]) {
    w[CACHE_KEY] = {
      icd10: null,
      icd10Promise: null,
      icd10Index: null,
      nhrpl: null,
      nhrplPromise: null,
    };
  }
  return w[CACHE_KEY] as CodeCacheStore;
}

/**
 * Build a prefix index: for each code, index its first 1 and 2 char prefixes.
 * This allows O(1) lookup for prefix-only queries instead of scanning 41K codes.
 */
function buildIcd10Index(codes: Icd10Code[]): Map<string, Icd10Code[]> {
  const index = new Map<string, Icd10Code[]>();
  for (const c of codes) {
    const code = c.code.toLowerCase();
    for (let len = 1; len <= Math.min(3, code.length); len++) {
      const prefix = code.slice(0, len);
      const bucket = index.get(prefix);
      if (bucket) bucket.push(c);
      else index.set(prefix, [c]);
    }
  }
  return index;
}

export function useIcd10Cache() {
  const { get } = useApi();
  const [ready, setReady] = useState(() => !!getStore().icd10);

  useEffect(() => {
    const store = getStore();
    if (store.icd10) {
      setReady(true);
      return;
    }

    if (!store.icd10Promise) {
      store.icd10Promise = get<Icd10Code[]>('/icd10').then(result => {
        if (result.success && result.data) {
          store.icd10 = result.data;
          store.icd10Index = buildIcd10Index(result.data);
          return result.data;
        }
        return [];
      }).catch(() => []);
    }

    store.icd10Promise.then(() => setReady(true));
  }, [get]);

  const search = useCallback((query: string, limit = 30): Icd10Code[] => {
    const store = getStore();
    const cache = store.icd10;
    if (!cache || !query) return [];
    const q = query.toLowerCase();

    // For short queries (1-3 chars), use prefix index for O(1) lookup
    if (q.length <= 3 && store.icd10Index) {
      const bucket = store.icd10Index.get(q) || [];
      return bucket.slice(0, limit);
    }

    // For longer queries, scan the full array (but still early-exit at limit)
    const results: Icd10Code[] = [];
    for (const c of cache) {
      if (
        c.code.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q)
      ) {
        results.push(c);
        if (results.length >= limit) break;
      }
    }
    return results;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const invalidate = useCallback(() => {
    const store = getStore();
    store.icd10 = null;
    store.icd10Promise = null;
    store.icd10Index = null;
    setReady(false);
  }, []);

  return { codes: getStore().icd10 || [], loading: !ready, search, invalidate };
}

export function useNhrplCache() {
  const { get } = useApi();
  const [ready, setReady] = useState(() => !!getStore().nhrpl);

  useEffect(() => {
    const store = getStore();
    if (store.nhrpl) {
      setReady(true);
      return;
    }

    if (!store.nhrplPromise) {
      store.nhrplPromise = get<NhrplCode[]>('/nhrpl').then(result => {
        if (result.success && result.data) {
          store.nhrpl = result.data;
          return result.data;
        }
        return [];
      }).catch(() => []);
    }

    store.nhrplPromise.then(() => setReady(true));
  }, [get]);

  const search = useCallback((query: string, limit = 30): NhrplCode[] => {
    const cache = getStore().nhrpl;
    if (!cache || !query) return [];
    const q = query.toLowerCase();
    const results: NhrplCode[] = [];
    for (const c of cache) {
      const codeLower = c.code.toLowerCase();
      let match: boolean;
      if (q.length < 2) {
        match = codeLower.startsWith(q);
      } else {
        match = codeLower.includes(q) || c.description.toLowerCase().includes(q) || c.category.toLowerCase().includes(q);
      }
      if (match) {
        results.push(c);
        if (results.length >= limit) break;
      }
    }
    return results;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const invalidate = useCallback(() => {
    const store = getStore();
    store.nhrpl = null;
    store.nhrplPromise = null;
    setReady(false);
  }, []);

  return { codes: getStore().nhrpl || [], loading: !ready, search, invalidate };
}
